import {gql} from '@apollo/client'
import React, {useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'

// import {useToast} from '../../lib/toast'
import {PreviewFlagMarker} from '../../../components/feature-flags'
import {Button} from '../../../components/library/buttons'
import {CopyInput} from '../../../components/library/copy'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResults,
  NoResultsPermissions
} from '../../../components/library/list'
import {Tag} from '../../../components/library/tag/tag'
import {OverflowFooter} from '../../../components/overflow'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'
import {
  IntegrationDetailFragment,
  useUpdateIntegrationMutation
} from './generated'
import {ConfirmIntegrationActionModal} from './modals/confirm'

/** TODO: standardise between both */
export const IntegrationView = ({
  environment,
  integrations,
  loading,
  sectionTitle,
  sectionDescription,
  renderEditModal,
  renderAddModal,
  renderConfirmModal,
  serviceUrl
}: {
  environment: EnvironmentNode
  integrations: IntegrationDetailFragment[]
  loading: boolean
  sectionTitle: string
  sectionDescription: string
  renderAddModal: (canEdit: boolean, handleClose: () => void) => React.ReactNode
  renderEditModal: (
    integration: IntegrationDetailFragment,
    canEdit: boolean,
    handleClose: () => void
  ) => React.ReactNode
  renderConfirmModal: (
    action: 'disable' | 'delete',
    integration: IntegrationDetailFragment,
    handleClose: () => void
  ) => React.ReactNode
  serviceUrl?: string
}) => {
  const canView = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.View
  )

  const canEdit = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.Admin
  )

  const [add, setAdd] = useState(false)
  const [edit, setEdit] = useState<IntegrationDetailFragment>()
  const [confirm, setConfirm] = useState<
    {action: 'disable' | 'delete'; item: IntegrationDetailFragment} | undefined
  >()

  const [update] = useUpdateIntegrationMutation()

  const columns: ListColumns<IntegrationDetailFragment> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: r => (
          <a
            onClick={() => {
              setEdit(r)
            }}
          >
            {r.name}
          </a>
        )
      },
      {
        id: 'linked',
        header: 'Linked card templates',
        accessor: r => r.associatedActionFlows?.length ?? 0
      },
      {
        id: 'enabled',
        header: 'Status',
        accessor: r =>
          r.enabled ? (
            <Tag appearance="green">Enabled</Tag>
          ) : (
            <Tag appearance="light">Disabled</Tag>
          )
      }
    ],
    []
  )

  return (
    <>
      <Section>
        <Helmet>
          <title>{sectionTitle}</title>
        </Helmet>
        <header>
          <IconHeading>
            <Icon name="building" /> {sectionTitle}
            <PreviewFlagMarker />
          </IconHeading>

          <nav>
            <Button
              disabled={!canEdit}
              onClick={() => {
                setAdd(true)
              }}
            >
              Add integration
            </Button>
          </nav>
        </header>

        <SectionInfo>
          <p>{sectionDescription}</p>
        </SectionInfo>

        {serviceUrl && (
          <Section>
            <CopyInput copyText="Copy Service URL" value={serviceUrl} />
          </Section>
        )}

        <SectionContent>
          <List
            columns={columns}
            rows={integrations}
            renderOverflow={(item, closeDropdown) => (
              <Overflow
                item={item}
                closeDropdown={closeDropdown}
                setEdit={setEdit}
                canEdit={canEdit}
                enable={integrationId => {
                  update({
                    variables: {
                      environmentId: environment.id,
                      id: integrationId,
                      enabled: true
                    }
                  })
                }}
                setDelete={integration => {
                  setConfirm({action: 'delete', item: integration})
                }}
                setDisable={integration => {
                  setConfirm({action: 'disable', item: integration})
                }}
              />
            )}
            loading={loading}
            renderEmpty={() =>
              canView ? (
                <NoResults
                  icon="building"
                  heading="No integrations"
                  message="No integrations configured"
                />
              ) : (
                <NoResultsPermissions />
              )
            }
          />
        </SectionContent>
      </Section>
      {/* Modal sections, scope for reuse */}
      {add && canEdit
        ? renderAddModal(
            canEdit,
            () => setAdd(false) // close handler
          )
        : null}
      {edit ? renderEditModal(edit, canEdit, () => setEdit(undefined)) : null}
      {confirm && canEdit
        ? renderConfirmModal(confirm.action, confirm.item, () => {
            setConfirm(undefined)
          })
        : null}
    </>
  )
}

type OverflowProps = {
  item: IntegrationDetailFragment
  closeDropdown: () => void
  setEdit: (obj: IntegrationDetailFragment) => void
  setDisable: (obj: IntegrationDetailFragment) => void
  setDelete: (obj: IntegrationDetailFragment) => void
  enable: (integrationId: string) => void
  canEdit: boolean
}
const Overflow = ({
  item,
  closeDropdown,
  setEdit,
  setDisable,
  setDelete,
  enable,
  canEdit
}: OverflowProps) => {
  return (
    <>
      <DropdownBody>
        <DropdownItem>
          <a
            onClick={() => {
              setEdit(item)
              closeDropdown()
            }}
          >
            {canEdit ? 'Edit' : 'View details'}
          </a>
        </DropdownItem>

        {canEdit ? (
          <DropdownItem>
            {item.enabled ? (
              <a
                onClick={() => {
                  setDisable(item)
                  closeDropdown()
                }}
              >
                Disable
              </a>
            ) : (
              <a
                onClick={() => {
                  enable(item.id)
                  closeDropdown()
                }}
              >
                Enable
              </a>
            )}
          </DropdownItem>
        ) : null}

        {canEdit && !item.enabled ? (
          <DropdownItem>
            <a
              onClick={() => {
                setDelete(item)
                closeDropdown()
              }}
            >
              Delete
            </a>
          </DropdownItem>
        ) : null}
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter id={item.id} created={item.created} />
      </DropdownFooter>
    </>
  )
}

gql`
  query GetIntegrations(
    $environmentId: String!
    $integrationType: IntegrationType!
  ) {
    integrationConfigurations(
      condition: {
        environmentId: $environmentId
        integrationType: $integrationType
      }
    ) {
      nodes {
        ...IntegrationDetail
      }
    }
  }

  mutation UpdateIntegration(
    $environmentId: String!
    $id: String!
    $name: String
    $configuration: JSON
    $enabled: Boolean
  ) {
    updateIntegrationConfiguration(
      input: {
        environmentId: $environmentId
        id: $id
        patch: {name: $name, configuration: $configuration, enabled: $enabled}
      }
    ) {
      integrationConfiguration {
        ...IntegrationDetail
      }
    }
  }

  mutation AddIntegration(
    $environmentId: String!
    $name: String!
    $configuration: JSON!
    $integrationType: IntegrationType!
  ) {
    createIntegrationConfiguration(
      input: {
        integrationConfiguration: {
          environmentId: $environmentId
          name: $name
          configuration: $configuration
          enabled: true
          integrationType: $integrationType
        }
      }
    ) {
      integrationConfiguration {
        ...IntegrationDetail
      }
    }
  }

  mutation DeleteIntegration($environmentId: String!, $id: String!) {
    deleteIntegrationConfiguration(
      input: {environmentId: $environmentId, id: $id}
    ) {
      integrationConfiguration {
        id
      }
    }
  }

  fragment IntegrationDetail on IntegrationConfiguration {
    id
    integrationType
    name
    configuration
    created
    environmentId
    enabled
    ...ConfirmIntegrationActionModalIntegration
  }

  ${ConfirmIntegrationActionModal.fragments.integration}
`
